import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Check, X, AlertTriangle, Scale, Vote, RefreshCw, 
  Users, MapPin, Building2, Landmark, FileText, Shield, ChevronRight,
  Sparkles, TrendingUp, Heart
} from 'lucide-react';

const ComparaisonPage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();

  // Translations
  const t = {
    fr: {
      breadcrumb: 'Comparaison',
      badge: 'Comprendre le changement',
      title: '3ᵉ République vs 4ᵉ République',
      subtitle: 'Ce qui change concrètement pour vous et pour la Côte d\'Ivoire',
      current: 'Aujourd\'hui',
      proposed: 'Demain',
      currentSubtitle: '3ᵉ République (2016)',
      proposedSubtitle: '4ᵉ République (Projet ADA)',
      ctaTitle: 'Convaincu par le changement ?',
      ctaSubtitle: 'Rejoignez les citoyens qui soutiennent la 4ᵉ République',
      ctaButton: 'Je soutiens ce projet',
      readConstitution: 'Lire la Constitution complète',
      categories: {
        eligibility: 'Éligibilité',
        mandates: 'Mandats présidentiels',
        territory: 'Organisation territoriale',
        senate: 'Sénat',
        constitutional: 'Révisions constitutionnelles',
        transparency: 'Transparence'
      }
    },
    en: {
      breadcrumb: 'Comparison',
      badge: 'Understanding the change',
      title: '3rd Republic vs 4th Republic',
      subtitle: 'What concretely changes for you and for Côte d\'Ivoire',
      current: 'Today',
      proposed: 'Tomorrow',
      currentSubtitle: '3rd Republic (2016)',
      proposedSubtitle: '4th Republic (ADA Project)',
      ctaTitle: 'Convinced by the change?',
      ctaSubtitle: 'Join the citizens who support the 4th Republic',
      ctaButton: 'I support this project',
      readConstitution: 'Read the full Constitution',
      categories: {
        eligibility: 'Eligibility',
        mandates: 'Presidential terms',
        territory: 'Territorial organization',
        senate: 'Senate',
        constitutional: 'Constitutional revisions',
        transparency: 'Transparency'
      }
    },
    pt: {
      breadcrumb: 'Comparação',
      badge: 'Compreender a mudança',
      title: '3ª República vs 4ª República',
      subtitle: 'O que muda concretamente para si e para a Costa do Marfim',
      current: 'Hoje',
      proposed: 'Amanhã',
      currentSubtitle: '3ª República (2016)',
      proposedSubtitle: '4ª República (Projeto ADA)',
      ctaTitle: 'Convencido pela mudança?',
      ctaSubtitle: 'Junte-se aos cidadãos que apoiam a 4ª República',
      ctaButton: 'Eu apoio este projeto',
      readConstitution: 'Ler a Constituição completa',
      categories: {
        eligibility: 'Elegibilidade',
        mandates: 'Mandatos presidenciais',
        territory: 'Organização territorial',
        senate: 'Senado',
        constitutional: 'Revisões constitucionais',
        transparency: 'Transparência'
      }
    }
  };

  const content = t[language] || t.fr;

  // Comparison data
  const comparisons = [
    {
      id: 'eligibility',
      icon: Vote,
      category: content.categories.eligibility,
      current: {
        title: language === 'fr' ? 'Système de parrainage actuel' : language === 'en' ? 'Current sponsorship system' : 'Sistema de patrocínio atual',
        points: [
          language === 'fr' ? 'Parrainage citoyen obligatoire' : language === 'en' ? 'Mandatory citizen sponsorship' : 'Patrocínio cidadão obrigatório',
          language === 'fr' ? 'Délais courts et contraignants' : language === 'en' ? 'Short and restrictive deadlines' : 'Prazos curtos e restritivos',
          language === 'fr' ? 'Processus complexe et coûteux' : language === 'en' ? 'Complex and costly process' : 'Processo complexo e caro'
        ],
        verdict: 'negative'
      },
      proposed: {
        title: language === 'fr' ? 'Parrainages citoyens simplifiés' : language === 'en' ? 'Simplified citizen sponsorships' : 'Patrocínios cidadãos simplificados',
        points: [
          language === 'fr' ? 'Campagne de collecte sur 3 mois' : language === 'en' ? 'Collection campaign over 3 months' : 'Campanha de recolha em 3 meses',
          language === 'fr' ? 'Tout électeur né ivoirien est éligible' : language === 'en' ? 'Any Ivorian-born voter is eligible' : 'Todo eleitor nascido marfinense é elegível',
          language === 'fr' ? 'Processus ouvert et transparent' : language === 'en' ? 'Open and transparent process' : 'Processo aberto e transparente'
        ],
        verdict: 'positive',
        article: 'Art. 47'
      }
    },
    {
      id: 'mandates',
      icon: RefreshCw,
      category: content.categories.mandates,
      current: {
        title: language === 'fr' ? '2 mandats maximum' : language === 'en' ? 'Maximum 2 terms' : 'Máximo 2 mandatos',
        points: [
          language === 'fr' ? 'Limitation à 2 mandats de 5 ans' : language === 'en' ? 'Limited to 2 five-year terms' : 'Limitado a 2 mandatos de 5 anos',
          language === 'fr' ? 'Décision imposée au peuple' : language === 'en' ? 'Decision imposed on the people' : 'Decisão imposta ao povo',
          language === 'fr' ? 'Instabilité potentielle' : language === 'en' ? 'Potential instability' : 'Instabilidade potencial'
        ],
        verdict: 'warning'
      },
      proposed: {
        title: language === 'fr' ? 'Le peuple décide seul' : language === 'en' ? 'The people decide alone' : 'O povo decide sozinho',
        points: [
          language === 'fr' ? 'Mandats de 5 ans, renouvelables' : language === 'en' ? '5-year terms, renewable' : 'Mandatos de 5 anos, renováveis',
          language === 'fr' ? 'Souveraineté populaire absolue' : language === 'en' ? 'Absolute popular sovereignty' : 'Soberania popular absoluta',
          language === 'fr' ? 'Le peuple sanctionne ou reconduit' : language === 'en' ? 'The people sanction or renew' : 'O povo sanciona ou reconduz'
        ],
        verdict: 'positive',
        article: 'Art. 55'
      }
    },
    {
      id: 'territory',
      icon: MapPin,
      category: content.categories.territory,
      current: {
        title: language === 'fr' ? 'Centralisation à Abidjan' : language === 'en' ? 'Centralization in Abidjan' : 'Centralização em Abidjan',
        points: [
          language === 'fr' ? 'Pouvoir concentré dans la capitale' : language === 'en' ? 'Power concentrated in the capital' : 'Poder concentrado na capital',
          language === 'fr' ? 'Régions dépendantes du gouvernement' : language === 'en' ? 'Regions dependent on government' : 'Regiões dependentes do governo',
          language === 'fr' ? 'Développement inégal du territoire' : language === 'en' ? 'Unequal territorial development' : 'Desenvolvimento territorial desigual'
        ],
        verdict: 'negative'
      },
      proposed: {
        title: language === 'fr' ? '31 Provinces autonomes' : language === 'en' ? '31 Autonomous provinces' : '31 Províncias autónomas',
        points: [
          language === 'fr' ? 'Autonomie fiscale et administrative' : language === 'en' ? 'Fiscal and administrative autonomy' : 'Autonomia fiscal e administrativa',
          language === 'fr' ? 'Budgets propres, décisions locales' : language === 'en' ? 'Own budgets, local decisions' : 'Orçamentos próprios, decisões locais',
          language === 'fr' ? 'Développement équilibré du pays' : language === 'en' ? 'Balanced development of the country' : 'Desenvolvimento equilibrado do país'
        ],
        verdict: 'positive',
        article: 'Art. 156'
      }
    },
    {
      id: 'senate',
      icon: Building2,
      category: content.categories.senate,
      current: {
        title: language === 'fr' ? 'Sénat nommé' : language === 'en' ? 'Appointed Senate' : 'Senado nomeado',
        points: [
          language === 'fr' ? '1/3 des sénateurs nommés par le Président' : language === 'en' ? '1/3 of senators appointed by the President' : '1/3 dos senadores nomeados pelo Presidente',
          language === 'fr' ? 'Chambre sous influence de l\'exécutif' : language === 'en' ? 'Chamber under executive influence' : 'Câmara sob influência do executivo',
          language === 'fr' ? 'Représentativité questionnable' : language === 'en' ? 'Questionable representativeness' : 'Representatividade questionável'
        ],
        verdict: 'negative'
      },
      proposed: {
        title: language === 'fr' ? 'Sénat territorial élu' : language === 'en' ? 'Elected territorial Senate' : 'Senado territorial eleito',
        points: [
          language === 'fr' ? '100% des sénateurs élus' : language === 'en' ? '100% of senators elected' : '100% dos senadores eleitos',
          language === 'fr' ? 'Représentation des 31 provinces' : language === 'en' ? 'Representation of 31 provinces' : 'Representação das 31 províncias',
          language === 'fr' ? 'Voix des territoires au Parlement' : language === 'en' ? 'Voice of territories in Parliament' : 'Voz dos territórios no Parlamento'
        ],
        verdict: 'positive',
        article: 'Art. 87'
      }
    },
    {
      id: 'constitutional',
      icon: FileText,
      category: content.categories.constitutional,
      current: {
        title: language === 'fr' ? 'Révision par les élus' : language === 'en' ? 'Revision by elected officials' : 'Revisão pelos eleitos',
        points: [
          language === 'fr' ? 'Initiative présidentielle ou parlementaire' : language === 'en' ? 'Presidential or parliamentary initiative' : 'Iniciativa presidencial ou parlamentar',
          language === 'fr' ? 'Citoyens exclus du processus' : language === 'en' ? 'Citizens excluded from the process' : 'Cidadãos excluídos do processo',
          language === 'fr' ? 'Risque de modification pour intérêts personnels' : language === 'en' ? 'Risk of modification for personal interests' : 'Risco de modificação para interesses pessoais'
        ],
        verdict: 'warning'
      },
      proposed: {
        title: language === 'fr' ? 'Panel citoyen de 150 personnes' : language === 'en' ? 'Citizen panel of 150 people' : 'Painel cidadão de 150 pessoas',
        points: [
          language === 'fr' ? 'Citoyens tirés au sort' : language === 'en' ? 'Randomly selected citizens' : 'Cidadãos sorteados',
          language === 'fr' ? 'Élaboration participative des révisions' : language === 'en' ? 'Participatory drafting of revisions' : 'Elaboração participativa das revisões',
          language === 'fr' ? 'Validation obligatoire par référendum' : language === 'en' ? 'Mandatory validation by referendum' : 'Validação obrigatória por referendo'
        ],
        verdict: 'positive',
        article: 'Art. 112'
      }
    },
    {
      id: 'transparency',
      icon: Shield,
      category: content.categories.transparency,
      current: {
        title: language === 'fr' ? 'Déclaration de patrimoine limitée' : language === 'en' ? 'Limited asset declaration' : 'Declaração de património limitada',
        points: [
          language === 'fr' ? 'Déclaration non publique' : language === 'en' ? 'Non-public declaration' : 'Declaração não pública',
          language === 'fr' ? 'Contrôle insuffisant' : language === 'en' ? 'Insufficient control' : 'Controlo insuficiente',
          language === 'fr' ? 'Enrichissement illicite difficile à prouver' : language === 'en' ? 'Illicit enrichment difficult to prove' : 'Enriquecimento ilícito difícil de provar'
        ],
        verdict: 'negative'
      },
      proposed: {
        title: language === 'fr' ? 'Transparence totale obligatoire' : language === 'en' ? 'Mandatory total transparency' : 'Transparência total obrigatória',
        points: [
          language === 'fr' ? 'Déclaration publique et vérifiable' : language === 'en' ? 'Public and verifiable declaration' : 'Declaração pública e verificável',
          language === 'fr' ? 'Haute Autorité de Transparence' : language === 'en' ? 'High Authority for Transparency' : 'Alta Autoridade de Transparência',
          language === 'fr' ? 'Sanctions automatiques en cas de fraude' : language === 'en' ? 'Automatic sanctions in case of fraud' : 'Sanções automáticas em caso de fraude'
        ],
        verdict: 'positive',
        article: 'Art. 78'
      }
    }
  ];

  const getVerdictStyles = (verdict, side) => {
    if (side === 'current') {
      if (verdict === 'negative') return { bg: 'bg-red-500/10', border: 'border-red-500/30', icon: X, iconColor: 'text-red-500' };
      if (verdict === 'warning') return { bg: 'bg-orange-500/10', border: 'border-orange-500/30', icon: AlertTriangle, iconColor: 'text-orange-500' };
      return { bg: 'bg-gray-500/10', border: 'border-gray-500/30', icon: null, iconColor: 'text-gray-500' };
    }
    return { bg: 'bg-[#0D9488]/10', border: 'border-[#0D9488]/30', icon: Check, iconColor: 'text-[#0D9488]' };
  };

  return (
    <div className={`pt-24 min-h-screen ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
      {/* Breadcrumb */}
      <div className={`border-b ${isDark ? 'bg-[#1C1917] border-white/10' : 'bg-[#F5F0E8] border-[#E8E0D4]'}`}>
        <div className="container mx-auto px-4 lg:px-10 py-3">
          <nav className={`flex items-center gap-2 text-sm ${isDark ? 'text-stone-400' : 'text-[#6B6B6B]'}`}>
            <Link to="/accueil" className={`hover:${isDark ? 'text-[#F7C948]' : 'text-[#5B2D8E]'}`}>Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/constitution" className={`hover:${isDark ? 'text-[#F7C948]' : 'text-[#5B2D8E]'}`}>Constitution</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-theme-primary">{content.breadcrumb}</span>
          </nav>
        </div>
      </div>

      {/* Hero Header */}
      <div className="bg-gradient-to-br from-[#5B2D8E] via-[#4A2472] to-[#3D1E5F] text-white py-16">
        <div className="container mx-auto px-4 lg:px-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-[#F7C948]" />
              <span className="text-[#F7C948] text-sm font-bold uppercase tracking-widest">{content.badge}</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-black mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              {content.title}
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              {content.subtitle}
            </p>
          </motion.div>

          {/* Legend */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10">
              <div className="w-4 h-4 rounded-full bg-red-500/50" />
              <span className="text-sm">{content.current}</span>
            </div>
            <div className="flex items-center gap-2">
              <ArrowRight className="w-6 h-6 text-[#F7C948]" />
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#0D9488]/20 border border-[#0D9488]/50">
              <div className="w-4 h-4 rounded-full bg-[#0D9488]" />
              <span className="text-sm">{content.proposed}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Cards */}
      <div className="container mx-auto px-4 lg:px-10 py-12">
        <div className="space-y-8 max-w-6xl mx-auto">
          {comparisons.map((item, index) => {
            const Icon = item.icon;
            const currentStyles = getVerdictStyles(item.current.verdict, 'current');
            const proposedStyles = getVerdictStyles(item.proposed.verdict, 'proposed');
            const CurrentVerdictIcon = currentStyles.icon;
            const ProposedVerdictIcon = proposedStyles.icon;

            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`rounded-2xl overflow-hidden border ${
                  isDark ? 'bg-[#1C1917] border-white/10' : 'bg-white border-[#E8E0D4]'
                }`}
              >
                {/* Category Header */}
                <div className={`px-6 py-4 border-b flex items-center gap-3 ${
                  isDark ? 'bg-[#292524] border-white/10' : 'bg-[#F5F0E8] border-[#E8E0D4]'
                }`}>
                  <div className="w-10 h-10 rounded-lg bg-[#5B2D8E] flex items-center justify-center">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-[#2D2D2D]'}`}>
                    {item.category}
                  </h3>
                </div>

                {/* Comparison Grid */}
                <div className="grid md:grid-cols-2">
                  {/* Current (3e République) */}
                  <div className={`p-6 border-b md:border-b-0 md:border-r ${
                    isDark ? 'border-white/10' : 'border-[#E8E0D4]'
                  }`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <span className={`text-xs font-bold uppercase tracking-wider ${
                          isDark ? 'text-stone-500' : 'text-[#9A9A9A]'
                        }`}>
                          {content.currentSubtitle}
                        </span>
                        <h4 className={`text-lg font-bold mt-1 ${isDark ? 'text-white' : 'text-[#2D2D2D]'}`}>
                          {item.current.title}
                        </h4>
                      </div>
                      {CurrentVerdictIcon && (
                        <div className={`p-2 rounded-full ${currentStyles.bg}`}>
                          <CurrentVerdictIcon className={`w-5 h-5 ${currentStyles.iconColor}`} />
                        </div>
                      )}
                    </div>
                    <ul className="space-y-2">
                      {item.current.points.map((point, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                            item.current.verdict === 'negative' ? 'bg-red-500' : 
                            item.current.verdict === 'warning' ? 'bg-orange-500' : 'bg-gray-400'
                          }`} />
                          <span className={`text-sm ${isDark ? 'text-stone-400' : 'text-[#6B6B6B]'}`}>
                            {point}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Proposed (4e République) */}
                  <div className={`p-6 ${proposedStyles.bg}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <span className="text-xs font-bold uppercase tracking-wider text-[#0D9488]">
                          {content.proposedSubtitle}
                        </span>
                        <h4 className={`text-lg font-bold mt-1 ${isDark ? 'text-white' : 'text-[#2D2D2D]'}`}>
                          {item.proposed.title}
                        </h4>
                      </div>
                      <div className="flex items-center gap-2">
                        {item.proposed.article && (
                          <span className="px-2 py-1 rounded text-xs font-bold bg-[#5B2D8E]/20 text-[#5B2D8E]">
                            {item.proposed.article}
                          </span>
                        )}
                        {ProposedVerdictIcon && (
                          <div className={`p-2 rounded-full bg-[#0D9488]/20`}>
                            <ProposedVerdictIcon className={`w-5 h-5 text-[#0D9488]`} />
                          </div>
                        )}
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {item.proposed.points.map((point, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className="w-4 h-4 mt-0.5 text-[#0D9488] flex-shrink-0" />
                          <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#2D2D2D]'}`}>
                            {point}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* CTA Section */}
      <div className={`py-16 ${isDark ? 'bg-[#1C1917]' : 'bg-[#F5F0E8]'}`}>
        <div className="container mx-auto px-4 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <Heart className="w-6 h-6 text-[#F7C948]" />
            </div>
            <h2 className={`text-3xl lg:text-4xl font-black mb-4 ${isDark ? 'text-white' : 'text-[#2D2D2D]'}`} style={{ fontFamily: 'var(--font-heading)' }}>
              {content.ctaTitle}
            </h2>
            <p className={`text-lg mb-8 ${isDark ? 'text-stone-400' : 'text-[#6B6B6B]'}`}>
              {content.ctaSubtitle}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/accueil#4e-republique"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#F7C948] text-[#0A0510] font-bold rounded-lg hover:bg-[#E5A700] transition-all hover:-translate-y-1"
                style={{ boxShadow: '0 10px 40px rgba(247, 201, 72, 0.3)' }}
              >
                {content.ctaButton}
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/constitution"
                className={`inline-flex items-center gap-2 px-8 py-4 font-bold rounded-lg border-2 transition-all hover:-translate-y-1 ${
                  isDark 
                    ? 'border-white/20 text-white hover:bg-white/10' 
                    : 'border-[#5B2D8E]/30 text-[#5B2D8E] hover:bg-[#5B2D8E]/10'
                }`}
              >
                <Scale className="w-5 h-5" />
                {content.readConstitution}
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ComparaisonPage;
